import React, { useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import FormControl from "@mui/material/FormControl";
import Paper from "@mui/material/Paper";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import BillingTypePriorityModal from "./BillingTypePriorityModal";
import { getBillingTypesArray } from "../../../utils/billingHelpers";

const STORAGE_LOCATIONS_OCCUPIED = "Storage Locations Occupied";

const billingTypeCategories = [
  {
    category: "By the Pallet",
    options: [
      {
        value: "By the Pallet",
        label: "By the Pallet",
        description: "Charge per pallet type received (Standard, Oversized, etc.)."
      },
      {
        value: "By the Pallet (Warehouse-Built)",
        label: "By the Pallet (Warehouse-Built)",
        description: "Charge per pallet that the warehouse builds from loose cartons/items into a pallet."
      }
    ]
  },
  {
    category: "By the _____",
    options: [
      {
        value: "By the Unit/Item",
        label: "By the Unit/Item",
        description: "Charge based on each individual unit received (mattress, chair, table, etc.)."
      },
      {
        value: "By the Vault",
        label: "By the Vault",
        description: "Charge per wooden storage vault used."
      }
    ]
  },
  {
    category: "By Square Footage",
    options: [
      {
        value: "Square Foot by Item Dimensions",
        label: "Square Foot by Item Dimensions",
        description: "Charge based on the footprint (length × width) of each item."
      },
      {
        value: "Square Foot by Location Dimensions",
        label: "Square Foot by Location Dimensions",
        description: "Charge based on the square footage of the assigned storage location (rack bay, vault, shelf, etc.)."
      }
    ]
  },
  {
    category: "By Cubic Footage",
    options: [
      {
        value: "Cubic Foot by Item Dimensions",
        label: "Cubic Foot by Item Dimensions",
        description: "Charge based on the full volume (length × width × height) of each item."
      },
      {
        value: "Cubic Foot by Location Dimensions",
        label: "Cubic Foot by Location Dimensions",
        description: "Charge based on the cubic footage of the assigned storage location (rack bay, vault, shelf, etc.)."
      }
    ]
  },
  {
    category: "By Storage Space Occupied",
    options: [
      {
        value: "Storage Locations Occupied",
        label: "Storage Locations Occupied",
        description: "Flat charge per assigned storage location (rack slot, vault, floor location, trailer, etc.)."
      }
    ]
  },
  {
    category: "Weight-Based",
    options: [
      {
        value: "Hundredweight",
        label: "Hundredweight",
        description: "Charge based on total weight, calculated per 100 lbs."
      }
    ]
  },
  {
    category: "Other",
    options: [
      {
        value: "Flat Rate/As Quoted",
        label: "Flat Rate/As Quoted",
        description: "One set price regardless of item count, weight, or storage used."
      },
      {
        value: "Flat Rate/As Quoted (Internal Work Orders)",
        label: "Flat Rate/As Quoted",
        description: "Flat rate determined per internal work order transaction."
      },
      {
        value: "Time and Materials (Blended Rate)",
        label: "Time and Materials (Blended Rate)",
        description: "Variable rates based on the time and materials used for each applicable transaction."
      },
      {
        value: "Time and Materials (Itemized Rates)",
        label: "Time and Materials (Itemized Rates)",
        description: "Separate itemized rates for installers, warehouse labor, and forklift operations."
      }
    ]
  }
];

export default function BillingTypeSelectionModal({ open, onClose, currentValue, category, onSelect, enabledBillingTypes = [], categoryDisplayText = null, inboundBillingTypes = null }) {
  // Handle both single values (legacy), arrays, and new object structure
  const initializeSelectedValues = () => {
    const values = getBillingTypesArray(currentValue);
    return category === "storage" && values.includes(STORAGE_LOCATIONS_OCCUPIED)
      ? [STORAGE_LOCATIONS_OCCUPIED]
      : values;
  };
  
  const [selectedValues, setSelectedValues] = useState(initializeSelectedValues);
  const [showPriorityModal, setShowPriorityModal] = useState(false);
  const storageLocationsOccupiedSelected =
    category === "storage" && selectedValues.includes(STORAGE_LOCATIONS_OCCUPIED);

  // Check if we should restrict selections based on Inbound types
  const inboundTypesArray = inboundBillingTypes ? getBillingTypesArray(inboundBillingTypes) : [];
  const shouldRestrictToInbound = category === 'storage' && inboundTypesArray.length > 1;

  // Determine if a checkbox should be disabled
  const isCheckboxDisabled = (optionValue) => {
    if (storageLocationsOccupiedSelected && optionValue !== STORAGE_LOCATIONS_OCCUPIED) {
      return true;
    }

    if (!shouldRestrictToInbound) return false;
    if (selectedValues.length === 0) return false;

    // If user has made at least one selection
    const firstSelection = selectedValues[0];
    const firstSelectionIsInbound = inboundTypesArray.includes(firstSelection);

    if (firstSelectionIsInbound) {
      // First selection is in Inbound - only allow other Inbound types
      return !inboundTypesArray.includes(optionValue) && !selectedValues.includes(optionValue);
    } else {
      // First selection is NOT in Inbound - disable all other checkboxes (force single selection)
      return !selectedValues.includes(optionValue);
    }
  };

  // Helper function to check if a billing type is enabled
  const isBillingTypeEnabled = (optionValue) => {
    const hasValidEnabledTypes = enabledBillingTypes.length > 0 && enabledBillingTypes.some(type => type && type.trim());
    if (!hasValidEnabledTypes) return true; // Show all if no enabled types

    if (enabledBillingTypes.includes(optionValue)) return true;

    // Special case: Backend "Time and Materials (T&M)" enables both frontend variants
    const isTimeAndMaterialsVariant = optionValue === 'Time and Materials (Blended Rate)' || optionValue === 'Time and Materials (Itemized Rates)';
    if (isTimeAndMaterialsVariant && enabledBillingTypes.includes('Time and Materials (T&M)')) {
      return true;
    }

    return false;
  };

  // Filter billing types based on category and company enabled billing types
  const filteredBillingTypeCategories = billingTypeCategories.map(categoryGroup => ({
    ...categoryGroup,
    options: categoryGroup.options.filter(option => {
      // For internal work orders, only show specific billing types
      if (category === 'internal') {
        // For internal work orders, we explicitly define allowed types and skip company filtering
        const allowedTypes = [
          'Time and Materials (Blended Rate)',
          'Time and Materials (Itemized Rates)',
          'Flat Rate/As Quoted (Internal Work Orders)'
        ];
        return allowedTypes.includes(option.value);
      }

      // Exclude Time and Materials billing types from storage category
      if (category === 'storage' && (option.value === 'Time and Materials (Blended Rate)' || option.value === 'Time and Materials (Itemized Rates)')) {
        return false;
      }

      // Exclude Internal Work Order specific billing types from non-internal categories
      if (category !== 'internal' && option.value === 'Flat Rate/As Quoted (Internal Work Orders)') {
        return false;
      }
      
      // Exclude "Storage Locations Occupied" from inbound and outbound categories (only show for storage)
      if ((category === 'inbound' || category === 'outbound') && option.value === 'Storage Locations Occupied') {
        return false;
      }
      
      // Filter by company enabled billing types
      if (!isBillingTypeEnabled(option.value)) {
        return false;
      }
      
      return true;
    })
  })).filter(categoryGroup => categoryGroup.options.length > 0); // Remove empty categories

  const handleCheckboxChange = (value) => {
    setSelectedValues(prev => {
      if (category === "storage" && value === STORAGE_LOCATIONS_OCCUPIED) {
        return prev.includes(value) ? [] : [value];
      }

      if (prev.includes(value)) {
        return prev.filter(v => v !== value);
      } else {
        return [...prev, value];
      }
    });
  };

  const handleSelect = () => {
    // For internal work orders, skip priority modal and save directly
    if (category === 'internal') {
      onSelect(selectedValues);
      onClose();
      return;
    }

    // If multiple billing types selected, show priority modal
    if (selectedValues.length > 1) {
      setShowPriorityModal(true);
    } else {
      // Single selection, no need for priority ordering
      onSelect(selectedValues);
      onClose();
    }
  };

  const handlePrioritySave = (billingTypeData) => {
    // billingTypeData now contains { types: [...], comments: {...} }
    onSelect(billingTypeData);
    setShowPriorityModal(false);
    onClose();
  };

  const handlePriorityCancel = () => {
    setShowPriorityModal(false);
  };

  const handleCancel = () => {
    setSelectedValues(initializeSelectedValues());
    onClose();
  };

  return (
    <Dialog 
      open={open} 
      maxWidth={false} 
      onClose={handleCancel}
      PaperProps={{
        sx: { width: "750px", maxWidth: "90vw" }
      }}
    >
      <DialogContent sx={{ bgcolor: "#f4f6f8" }}>
        <DialogContentText>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
            <Box width={40} />
            <Box textAlign="center">
              <Typography fontSize={22} fontWeight={400}>
                Select Billing Type(s)
              </Typography>
              {categoryDisplayText && (
                <Typography fontSize={20} fontWeight={500} color="text.primary" mt={0.5}>
                  for {categoryDisplayText}
                </Typography>
              )}
            </Box>
            <IconButton color="textPrimary" onClick={handleCancel}>
              <CloseIcon />
            </IconButton>
          </Box>
          
          <FormControl component="fieldset" sx={{ width: "100%" }}>
            {storageLocationsOccupiedSelected && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Storage Locations Occupied bills every eligible asset by its assigned location. It
                cannot be combined with other storage billing types.
              </Alert>
            )}
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {filteredBillingTypeCategories.map((category) => (
                <Paper key={category.category}>
                  <Box p={2}>
                    <Typography fontWeight={500} mb={2}>
                      {category.category}
                    </Typography>
                    
                    <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                      {category.options.map((option) => (
                        <FormControlLabel
                          key={option.value}
                          control={
                            <Checkbox 
                              size="small" 
                              checked={selectedValues.includes(option.value)}
                              onChange={() => handleCheckboxChange(option.value)}
                              disabled={isCheckboxDisabled(option.value)}
                            />
                          }
                          label={
                            <Box sx={{ ml: 1 }}>
                              <Typography sx={{ fontWeight: 400, fontSize: 15 }}>
                                {option.label}
                              </Typography>
                              <Typography 
                                sx={{ 
                                  fontSize: 14, 
                                  color: "text.secondary",
                                  mt: 0.5,
                                  lineHeight: 1.4
                                }}
                              >
                                {option.description}
                              </Typography>
                            </Box>
                          }
                          sx={{ 
                            alignItems: "flex-start",
                            margin: 0,
                            "& .MuiFormControlLabel-label": {
                              mt: -0.5
                            }
                          }}
                        />
                      ))}
                    </Box>
                  </Box>
                </Paper>
              ))}
            </Box>
          </FormControl>

          <Box 
            mt={3} 
            display="flex" 
            justifyContent="flex-end" 
            sx={{
              position: "sticky",
              bottom: 0,
              bgcolor: "background.paper",
              borderTop: 1,
              borderColor: "divider",
              p: 2,
              zIndex: 1,
            }}
          >
            <Button 
              onClick={handleCancel}
              variant="outlined"
              color="primary"
              sx={{ mr: 1 }}
            >
              Close
            </Button>
            <Button 
              onClick={handleSelect}
              variant="contained"
              color="primary"
              disabled={selectedValues.length === 0}
            >
              Select
            </Button>
          </Box>
        </DialogContentText>
      </DialogContent>

      <BillingTypePriorityModal
        open={showPriorityModal}
        onClose={handlePriorityCancel}
        selectedBillingTypes={currentValue && typeof currentValue === 'object' && currentValue.types 
          ? { types: selectedValues, comments: currentValue.comments || {} }
          : selectedValues}
        onSave={handlePrioritySave}
        category={category}
      />
    </Dialog>
  );
}
